import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import CapitalBalanceRow from './CapitalBalanceRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ capitalBalance }) =>
  capitalBalance.map(capital => (
    <CapitalBalanceRow key={capital.id} {...capital} />
  ));

Tbody.propTypes = {
  capitalBalance: PropTypes.array.isRequired
};

const CapitalBalanceTable = ({
  capitalBalance,
  count,
  page,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(
    () => () => (
      <TableRow sx={{ '& .MuiTableCell-head': { whiteSpace: 'nowrap' } }}>
        <TableCell
          variant="head"
          key="client"
          style={{ width: '10%' }}
          align="center"
        >
          {t('COMMON.CLIENT')}
        </TableCell>
        <TableCell variant="head" key="contract" align="center">
          {t('COMMON.CONTRACT')}
        </TableCell>
        <TableCell variant="head" key="equipment" align="left">
          {t('COMMON.EQUIPMENT')}
        </TableCell>
        <TableCell
          variant="head"
          key="period"
          style={{ width: '5%' }}
          align="center"
        >
          {t('BILLING_VIEW.PERIOD')}
        </TableCell>
        <TableCell variant="head" key="initialBalance" align="center">
          {t('CAPITAL_BALANCE.INITIAL_BALANCE')}
        </TableCell>
        <TableCell variant="head" key="equipmentPrice" align="center">
          {t('COMMON.CONTRACT_REGISTRATION')}
        </TableCell>
        <TableCell variant="head" key="initialPayment" align="center">
          {t('CAPITAL_BALANCE.INITIAL_PAYMENT')}
        </TableCell>
        <TableCell variant="head" key="payment" align="center">
          {t('CAPITAL_BALANCE.AMORTIZATION')}
        </TableCell>
        <TableCell variant="head" key="contribution" align="center">
          {t('CAPITAL_BALANCE.CAPITAL_CONTRIBUTION')}
        </TableCell>
        <TableCell variant="head" key="residual" align="center">
          {t('CAPITAL_BALANCE.RESIDUAL_PAYMENT')}
        </TableCell>
        <TableCell variant="head" key="finalBalance" align="center">
          {t('CAPITAL_BALANCE.FINAL_BALANCE')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(() => () => <Tbody capitalBalance={capitalBalance} />, [
    capitalBalance
  ]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
      />
    ),
    [page, count, handleChangePage]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

CapitalBalanceTable.propTypes = {
  capitalBalance: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool
};

CapitalBalanceTable.defaultProps = {
  count: 0,
  loading: false
};

export default CapitalBalanceTable;
